import React from 'react'
import { Formik, FormikConfig } from 'formik'
import { useNavigate } from 'react-router'
import { useCreateWaitlist } from '../../api/bookings/useCreateWaitlist'
import { useIsMobile } from '../../hooks/useIsMobile'
import { getCompiledGMAdress } from '../../utils/compileGMAdress'
import { RestaurantLocation } from '../RestaurantLocation/RestaurantLocation'
import { Footer } from '../Footer'
import {
  getInitialFormValues,
  WaitlistDetailEntryViewFormValues
} from './models'
import { validationSchema } from './validationSchema'
import { WaitlistDetailEntryViewForm } from './WaitlistDetailEntryViewForm/WaitlistDetailEntryViewForm'
import { ContentWrapper } from '../ContentWrapper'
import { LoadingView } from '../LoadingView/LoadingView'
import { RestaurantInfo } from '../../api/restaurant/getRestaurant'
import { NewLineFormatter } from '../NewLineFormatter/NewLineFormatter'
import { makeIntlPhoneNumberString } from '@toasttab/buffet-pui-phone-utilities'
import { useRestaurantContext } from '../../contexts/RestaurantContext'
import { track } from '@toasttab/do-secundo-analytics'
import { AnalyticsEvents } from '../../utils/analyticsEvents'
import { Alert } from '@toasttab/buffet-pui-alerts'
import { useBanquetProps } from 'banquet-runtime-modules'
import { useSetupRecaptcha } from '@local/recaptcha'
import { GOOGLE_RECAPTCHA_KEY } from '../../api/recaptcha/constants'

export interface WaitlistDetailEntryViewProps {
  restaurant: RestaurantInfo
}

export function WaitlistDetailEntryView({
  restaurant
}: WaitlistDetailEntryViewProps) {
  const initialValues = getInitialFormValues(restaurant)

  const recaptchaEnabled = useBanquetProps().featureFlags?.['nv1-recaptcha']!
  // Kicks off loading recaptcha in the background; useScript will ensure we don't double-load
  const recaptchaLoadingStatus = useSetupRecaptcha(
    GOOGLE_RECAPTCHA_KEY,
    recaptchaEnabled
  )
  const createWaitlistMutation = useCreateWaitlist(recaptchaEnabled)
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { shortUrl } = useRestaurantContext()

  if (!restaurant) {
    return <LoadingView />
  }

  const { addressWithoutRestaurantName } = getCompiledGMAdress(restaurant!)

  const onSubmit: FormikConfig<WaitlistDetailEntryViewFormValues>['onSubmit'] =
    (values, formikHelpers) => {
      return createWaitlistMutation.mutateAsync(
        {
          restaurantGuid: restaurant!.guid,
          params: {
            partySize: values.partySize.toString(),
            bookingNotes: values.specialRequests,
            requestedServiceAreaGroups: values.requestedServiceAreaGroups,
            specialOccasion: values.specialOccasion,
            guest: {
              firstName: values.firstName,
              lastName: values.lastName,
              email: values.email ?? '',
              phoneNumber: makeIntlPhoneNumberString(values.phoneNumber)
            }
          }
        },
        {
          onSettled() {
            formikHelpers.setSubmitting(false)
          },
          onSuccess: (resp) => {
            track(AnalyticsEvents.JOINED_WAITLIST, {
              partySize: values.partySize.toString()
            })
            navigate(`/${shortUrl}/bookings/${resp.results[0].guid}`)
          }
        }
      )
    }

  return (
    <>
      <ContentWrapper>
        <div className='w-full md:w-2/3'>
          <div className='bg-white shadow-xl rounded-md relative p-4 md:p-6 md:mt-4'>
            <div className='type-large text-dark-gray border-b border-bg-darken-8 font-semibold pb-3 md:pb-3.5'>
              Join Waitlist
            </div>
            <div className='flex flex-col gap-4'>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                <WaitlistDetailEntryViewForm
                  restaurant={restaurant!}
                  isLoading={
                    createWaitlistMutation.isLoading ||
                    recaptchaLoadingStatus === 'loading'
                  }
                />
              </Formik>
              {createWaitlistMutation.isError ? (
                <Alert variant='error'>
                  An error occurred while trying to create your waitlist booking
                </Alert>
              ) : null}
            </div>
          </div>
          <div className='mt-8'>
            <div className='text-default font-semibold border-b border-bg-darken-8'>
              Waitlist Policy
            </div>
            <div className='text-secondary my-4'>
              <NewLineFormatter text={restaurant.waitlistPolicy} />
            </div>
          </div>
          {restaurant.description?.trim() ? (
            <div className='mt-8'>
              <div className='text-default font-semibold border-b border-bg-darken-8'>
                About the Restaurant
              </div>
              <div className='text-secondary my-4'>
                <NewLineFormatter text={restaurant.description} />
              </div>
            </div>
          ) : null}
        </div>
        {!isMobile && (
          <div className='flex flex-col relative mt-4 lg:w-96 md:w-1/3'>
            <RestaurantLocation
              phone={restaurant!.phoneNumber}
              site={restaurant!.websiteUrl}
              address={addressWithoutRestaurantName}
            />
          </div>
        )}
      </ContentWrapper>
      <Footer restaurant={restaurant} />
    </>
  )
}
